import { useEffect } from "react";

import { useAuth } from "@/context/AuthContext";

/**
 * Sube al servidor lo ganado en los minijuegos del mundo. No pinta nada.
 *
 * DÓNDE VA Y POR QUÉ. En el armazón, dentro del router y fuera de las rutas, al lado de
 * `CuentaPantallas` y `Felicitacion`, y por la misma razón que está escrita ahí arriba: **una
 * cosa que puede pasar en cualquier momento se monta donde está siempre**. Se gana en el
 * laberinto (`/laberinto`), en la pesca, en el Recorrido, en el Pórtico y en la Fragua (los dos
 * dentro de la Explanada): cinco sitios y dos pantallas distintas. Montarlo en el mundo habría
 * dejado el laberinto fuera, que es justo el único minijuego que hoy juega una cuenta gratis.
 *
 * SIN SESIÓN NO HACE NADA, y así no se pide nada en la portada ni en los precios. Las tres rutas
 * piden cuenta, así que sin ella sólo habría tres 401 por visita.
 *
 * Y NO IMPORTA NADA PESADO ARRIBA. Este fichero entra en la primera carga —lo importa `App.js` a
 * pelo, como el contador de pantallas— y del presupuesto de 260 kB quedan pocos kB libres. Así
 * que `victorias.mjs`, su costura y el cliente de `mundo` entran por `import()` DENTRO del
 * efecto, o sea sólo cuando hay sesión. No se usa `lazy()` porque esto no pinta: `lazy` sirve
 * para diferir algo que se dibuja, y aquí no hay nada que dibujar ni `Suspense` que esperar.
 *
 * LO PRIMERO, ANTES DE ENGANCHAR Y DE JUNTAR, ES MIRAR DE QUIÉN ES EL NAVEGADOR. En un ordenador
 * compartido lo guardado puede ser de la cuenta anterior, y subirlo sería regalárselo a esta
 * (`lib/duenoDelNavegador.mjs`). Va aquí porque esto es lo único que corre cada vez que cambia la
 * cuenta. Y ENTRA POR `import()` COMO LO DEMÁS: se escribió primero arriba, síncrono, y la
 * primera carga se quedó a 0,3 kB del límite. Lo que importa no es que corra antes que nada, es
 * que corra antes de que la costura lea la caché, y eso lo asegura el orden de abajo.
 */
export default function VictoriasEnLaNube() {
  const { user } = useAuth();
  // `user?.id` Y NO `user`: el objeto se vuelve a crear en cada refresco de la sesión, y con el
  // objeto como dependencia esto se desengancharía y volvería a juntar en cada uno.
  const quien = user?.id || null;

  useEffect(() => {
    if (!quien) return undefined;
    let vivo = true;
    let suelta = () => {};

    (async () => {
      let costura;
      let almacenes;
      let puente;
      let cola;
      let duenos;
      try {
        [duenos, costura, almacenes, puente, cola] = await Promise.all([
          import("@/lib/duenoDelNavegador.mjs"),
          import("@/lib/victoriasDelServidor.mjs"),
          import("@/lib/victorias.mjs"),
          import("@/lib/api/mundo"),
          import("@/lib/colaDeFelicitaciones.mjs"),
        ]);
      } catch {
        // Un trozo que no llega -red a medias, despliegue en marcha- no puede romper la pantalla
        // en la que esté el alumno. Lo ganado se sigue guardando en el navegador como siempre.
        return;
      }
      if (!vivo) return;

      // DE QUIÉN ES LO GUARDADO, ANTES DE TOCARLO. Si es de otra cuenta, se borra aquí mismo.
      const { local, sesion } = duenos.almacenesDelNavegador();
      const dueno = duenos.asegura(local, sesion, quien);

      // SE ENGANCHA ANTES DE JUNTAR, y el orden es lo único delicado de este fichero: entre la
      // subida y su respuesta el alumno puede ganar una partida, y si todavía no hubiera nadie
      // escuchando, esa victoria no se mandaría. Enganchado antes, se manda; y si falla, la
      // carrera la recoge `sincroniza`, que vuelve a juntar al ver que la caché cambió.
      // `canta` ES LA COLA QUE YA EXISTE, y entra por el puente como las otras dos: así la
      // costura se sigue probando en node, sin React y sin cartel. Quien decide si hay algo que
      // cantar es el servidor, que es quien sabe si el premio llegó al techo del día.
      suelta = costura.engancha({
        gana: puente.postVictoria,
        canta: (fiesta) => cola.felicita(fiesta),
      });
      // "nuevo" es un navegador sin marca de dueño: lo que haya en la caché puede ser de otra
      // persona, así que esa vez se pregunta sin subir. Después ya habrá marca.
      await costura.sincroniza(almacenes.almacenDelNavegador(),
        { junta: puente.juntaVictorias }, { subir: dueno !== "nuevo" });
    })();

    return () => { vivo = false; suelta(); };
  }, [quien]);

  return null;
}
